import {ScrollService} from "../../../common/scroll";
import {resolve} from "../../../container";
import {html, type TemplateResult} from "lit";
import {customElement, property, state} from "lit/decorators.js";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import {Dictionary} from "../dictionary";
import {Resolution} from "../../../common/resolution";
import {BOOTSTRAP} from "../../../common/resolutionConstants";
import {classMap} from "lit/directives/class-map.js";
import {EventBus} from "../../../common/eventBus";
import {FOOTNOTE_REFERENCE_CLICK_EVENT} from "./footnotes";

import {ManagingResources} from "../../../common/lifetime";
import type {Nullable} from "../../../bootstrap/common/types";
import {trackingButtonInteract} from "../../../tracking/trackingAttributes";
import {UnLitElement} from "../../../common/elements";

const FOOTNOTE_SCROLL_MARGIN = 15;

export type FootnoteElement = {
    text: string;
    number: number;
    truncatedText: string;
};

@customElement("eop-footnote-text")
export class EopFootnoteText extends ManagingResources(UnLitElement) {

    @property()
    private footnote: FootnoteElement;
    @state()
    private isShortened: boolean;

    private dictionary: Dictionary;

    public constructor(
        private resolution: Resolution = resolve(Resolution),
        private eventBus: EventBus = resolve(EventBus),
        private scrollService: ScrollService = resolve(ScrollService)
    ) {
        super();
        this.isShortened = false;
    }

    public connectedCallback(): void {
        super.connectedCallback();

        this.dictionary = Dictionary.of(this);
        this.resolution.onBootstrapBreakpointChange(() => this.toggleExpansion(), this);
        this.toggleExpansion();

        this.eventBus.on(FOOTNOTE_REFERENCE_CLICK_EVENT, event => {
            if (event.data.id === this.id) {
                void this.scrollService.scrollToElement(this, FOOTNOTE_SCROLL_MARGIN);
                this.expand();
            }
        }, this);
    }

    public render(): TemplateResult {
        const classes = {
            "content": true,
            "responsive": this.canBeTruncated()
        };
        return html`
            <span class=${classMap(classes)}>
                <span class="counter">${this.footnote.number}</span>
                ${this.renderFootnoteText()}
            </span>
        `;
    }

    private toggle(): void {
        this.isShortened = !this.isShortened;
    }

    private expand(): void {
        this.isShortened = false;
    }

    private canBeTruncated(): boolean {
        return this.footnote.text.length > this.footnote.truncatedText.length
            && this.resolution.upTo(BOOTSTRAP.XS);
    }

    private renderFootnoteText(): TemplateResult {
        const text = (this.canBeTruncated() && this.isShortened)
            ? this.footnote.truncatedText
            : this.footnote.text;
        return html`<span>${unsafeHTML(text)}${this.renderToggleButton()}</span>`;
    }

    private renderToggleButton(): Nullable<TemplateResult> {
        if (!this.canBeTruncated()) {
            return null;
        }

        if (this.isShortened) {
            return html`
                <button type="button" class="expand" @click=${this.toggle} ${trackingButtonInteract("footnote-expand: no. " + this.footnote.number)}
                >${this.dictionary.translate("MSG_MORE")}</button>`;
        } else {
            return html`
                <button type="button" class="shrink" @click=${this.toggle} ${trackingButtonInteract("footnote-shrink: no. " + this.footnote.number)}
                >${this.dictionary.translate("MSG_LESS")}</button>`;
        }
    }

    private toggleExpansion(): void {
        this.isShortened = this.resolution.upTo(BOOTSTRAP.XS);
    }
}